// Import types
import {
  StackModalAction,
  StackModalActionType,
  StackModalState,
} from '../types';

// Import utility functions
import {
  addControlMode,
  duplicateControlMode,
  removeControlMode,
  updateControlModeEnabled,
  updateControlModeName,
  updateControlModeStackAlias,
} from '../utils/controlModeHelpers';
import { deleteSection, updateFieldValue } from '../utils/fieldHelpers';
import { mergeTemplatesIntoControlMode } from '../utils/templateHelpers';

// Define the initial state
export const initialState: StackModalState = {
  stackName: '',
  nameError: '',
  validationErrors: {},
  alertMessage: null,
  controlModes: [],
  isSaving: false,
  isTemplatePickerOpen: false,
  currentControlModeIndex: null,
  isTemplateConfirmOpen: false,
  templatesForConfirmation: [],
  controlModeIndexForConfirmation: null,
  templateIdsForConfirmation: undefined,
  isDeleteConfirmOpen: false,
  controlModeToDelete: null,
};

// Define the reducer function
export const stackModalReducer = (
  state: StackModalState,
  action: StackModalAction,
): StackModalState => {
  switch (action.type) {
    // Stack form actions
    case StackModalActionType.SET_STACK_NAME:
      return {
        ...state,
        stackName: action.payload,
        nameError: action.payload.trim() ? '' : state.nameError,
        alertMessage: action.payload.trim() ? null : state.alertMessage,
      };

    case StackModalActionType.SET_NAME_ERROR:
      return {
        ...state,
        nameError: action.payload,
      };

    case StackModalActionType.SET_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: action.payload,
      };

    case StackModalActionType.CLEAR_VALIDATION_ERROR: {
      const updatedErrors = { ...state.validationErrors };
      delete updatedErrors[action.payload];
      return {
        ...state,
        validationErrors: updatedErrors,
      };
    }

    case StackModalActionType.CLEAR_CONTROL_MODE_VALIDATION_ERROR: {
      const { index, field } = action.payload;
      const updatedErrors = { ...state.validationErrors };

      if (field) {
        delete updatedErrors[`controlMode_${index}_${field}`];
      } else {
        Object.keys(updatedErrors).forEach((key) => {
          if (key.startsWith(`controlMode_${index}_`)) {
            delete updatedErrors[key];
          }
        });
      }

      return {
        ...state,
        validationErrors: updatedErrors,
        alertMessage: null,
      };
    }

    case StackModalActionType.SHOW_ALERT:
      return {
        ...state,
        alertMessage: action.payload,
      };

    case StackModalActionType.CLEAR_ALERT:
      return {
        ...state,
        alertMessage: null,
      };

    // Control modes actions
    case StackModalActionType.SET_CONTROL_MODES:
      return {
        ...state,
        controlModes: action.payload,
      };

    case StackModalActionType.ADD_CONTROL_MODE:
      return {
        ...state,
        controlModes: addControlMode(state.controlModes),
      };

    case StackModalActionType.DUPLICATE_CONTROL_MODE:
      return {
        ...state,
        controlModes: duplicateControlMode(state.controlModes, action.payload),
      };

    case StackModalActionType.UPDATE_CONTROL_MODE_NAME: {
      const { index, name } = action.payload;
      return {
        ...state,
        controlModes: updateControlModeName(state.controlModes, index, name),
      };
    }

    case StackModalActionType.UPDATE_CONTROL_MODE_ALIAS: {
      const { index, name } = action.payload;
      return {
        ...state,
        controlModes: updateControlModeStackAlias(
          state.controlModes,
          index,
          name,
        ),
      };
    }

    case StackModalActionType.UPDATE_CONTROL_MODE_ENABLED: {
      const { index, enabled } = action.payload;
      return {
        ...state,
        controlModes: updateControlModeEnabled(
          state.controlModes,
          index,
          enabled,
        ),
      };
    }

    case StackModalActionType.UPDATE_FIELD_VALUE: {
      const { controlModeIndex, sectionIndex, fieldIndex, newValue } =
        action.payload;
      return {
        ...state,
        controlModes: updateFieldValue(
          state.controlModes,
          controlModeIndex,
          sectionIndex,
          fieldIndex,
          newValue,
        ),
      };
    }

    case StackModalActionType.DELETE_SECTION: {
      const { controlModeIndex, sectionIndex } = action.payload;
      return {
        ...state,
        controlModes: deleteSection(
          state.controlModes,
          controlModeIndex,
          sectionIndex,
        ),
      };
    }

    // Handle templates selected from TemplatePicker component
    case StackModalActionType.TEMPLATES_SELECTED: {
      const { templates, controlModeIndex, templateIds } = action.payload;

      if (!templates || templates.length === 0) {
        return state;
      }

      const updatedControlModes = mergeTemplatesIntoControlMode(
        state.controlModes,
        controlModeIndex,
        templates,
        templateIds,
      );

      // Clear validation error if the control mode now has sections
      let updatedValidationErrors = { ...state.validationErrors };
      const updatedControlMode = updatedControlModes[controlModeIndex];

      if (
        updatedControlMode?.sections?.length > 0 &&
        updatedValidationErrors[`controlMode_${controlModeIndex}_sections`]
      ) {
        delete updatedValidationErrors[
          `controlMode_${controlModeIndex}_sections`
        ];
      }

      return {
        ...state,
        controlModes: updatedControlModes,
        validationErrors: updatedValidationErrors,
      };
    }

    // Handle delete control mode from DeleteConfirmationDialog component
    case StackModalActionType.REMOVE_CONTROL_MODE: {
      const index = action.payload;

      // Remove the control mode
      const updatedControlModes = removeControlMode(state.controlModes, index);

      // Clear validation errors for the deleted control mode
      const updatedValidationErrors = { ...state.validationErrors };
      Object.keys(updatedValidationErrors).forEach((key) => {
        if (key.startsWith(`controlMode_${index}_`)) {
          delete updatedValidationErrors[key];
        }
      });

      return {
        ...state,
        controlModes: updatedControlModes,
        validationErrors: updatedValidationErrors,
        alertMessage: null,
      };
    }

    // Save actions
    case StackModalActionType.SET_IS_SAVING:
      return {
        ...state,
        isSaving: action.payload,
      };

    case StackModalActionType.INITIALIZE_FORM:
      return {
        ...state,
        stackName: action.payload.stackName,
        controlModes: action.payload.controlModes,
      };

    case StackModalActionType.RESET_FORM:
      return {
        ...state,
        validationErrors: {},
        alertMessage: null,
        isSaving: false,
        stackName: '',
        controlModes: [],
        nameError: '',
        isDeleteConfirmOpen: false,
        controlModeToDelete: null,
        isTemplatePickerOpen: false,
        currentControlModeIndex: null,
        isTemplateConfirmOpen: false,
        templatesForConfirmation: [],
        controlModeIndexForConfirmation: null,
        templateIdsForConfirmation: undefined,
      };

    // Template picker actions
    case StackModalActionType.OPEN_TEMPLATE_PICKER:
      return {
        ...state,
        isTemplatePickerOpen: true,
        currentControlModeIndex: action.payload,
      };

    case StackModalActionType.CLOSE_TEMPLATE_PICKER:
      return {
        ...state,
        isTemplatePickerOpen: false,
        currentControlModeIndex: null,
      };

    // Template confirmation dialog actions
    case StackModalActionType.OPEN_TEMPLATE_CONFIRM:
      return {
        ...state,
        isTemplateConfirmOpen: true,
        templatesForConfirmation: action.payload.templates,
        controlModeIndexForConfirmation: action.payload.controlModeIndex,
        templateIdsForConfirmation: action.payload.templateIds,
      };

    case StackModalActionType.CLOSE_TEMPLATE_CONFIRM:
      return {
        ...state,
        isTemplateConfirmOpen: false,
        templatesForConfirmation: [],
        controlModeIndexForConfirmation: null,
        templateIdsForConfirmation: undefined,
      };

    // Delete confirmation dialog actions
    case StackModalActionType.OPEN_DELETE_CONFIRM:
      return {
        ...state,
        isDeleteConfirmOpen: true,
        controlModeToDelete: action.payload,
      };

    case StackModalActionType.CLOSE_DELETE_CONFIRM:
      return {
        ...state,
        isDeleteConfirmOpen: false,
        controlModeToDelete: null,
      };

    default:
      return state;
  }
};
